import getPublicKey from '../../api/getPublicKey';

/* This function converts the PEM format from the backend to a usable Crypto key needed for web crypto */
const importPemPublicKey = async (pem: string) => {
    try {
        // Remove header, footer, and line breaks
        const pemBody = pem
            .replace(/-----BEGIN PUBLIC KEY-----/, '')
            .replace(/-----END PUBLIC KEY-----/, '')
            .replace(/[\r\n\s]+/g, '');

        // Decode base64 to binary
        const binaryDer = Uint8Array.from(atob(pemBody), (c) => c.charCodeAt(0));

        // Import as CryptoKey
        return await crypto.subtle.importKey(
            'spki', // format for public keys
            binaryDer,
            {
                name: 'RSA-OAEP',
                hash: 'SHA-256',
            },
            false, // extractable
            ['encrypt'], // key usages
        );
    } catch (error) {
        console.error('Failed to import PEM public key:', error);
        throw new Error('Invalid public key format or unsupported key algorithm.');
    }
};

/* utility functions */
const stringToBase64 = (str: string) => {
    const u8 = new TextEncoder().encode(str);
    const bin = String.fromCharCode(...u8);
    return btoa(bin);
};
// buffer to b64 (potential to merge logic with string to base 64)
export const toB64 = (u8: Uint8Array) => btoa(String.fromCharCode(...u8));

/* Build the header for backend to process cek with the private key */
const buildHeader = async ({ b64Cek, b64IvConcat }: { b64Cek: string; b64IvConcat: string }) => {
    try {
        const data = await getPublicKey();
        const KeyId = data.key.keyid;
        const keyId_b64 = stringToBase64(KeyId);
        const publicKeyPem = data.key.pemContent;
        const CEK = Uint8Array.from(atob(b64Cek), (c) => c.charCodeAt(0));

        const importedKey = await importPemPublicKey(publicKeyPem);

        /* Encrypt CEK with Public Key */
        const encryptedCEK = await window.crypto?.subtle.encrypt(
            { name: 'RSA-OAEP' },
            importedKey, // a CryptoKey object with "encrypt" usage
            CEK, // an ArrayBuffer or TypedArray (e.g., Uint8Array)
        );

        const hashBuffer = await crypto.subtle.digest('SHA-256', CEK);

        const encryptedCEK_b64 = toB64(new Uint8Array(encryptedCEK));
        const hashedCEK_b64 = toB64(new Uint8Array(hashBuffer));

        return {
            'x-crypto-key': `${keyId_b64}.${encryptedCEK_b64}.${hashedCEK_b64}.${b64IvConcat}.GCM-256`,
        };
    } catch (error) {
        console.error('Error building x-crypto-key header:', error);
        throw new Error('Failed to build encryption header.');
    }
};

/* Main function for encrypting data */
export const encrypt = async ({ data }: { data: object }) => {
    try {
        const encoder = new TextEncoder();
        const cekRaw = crypto.getRandomValues(new Uint8Array(32));
        const iv12 = crypto.getRandomValues(new Uint8Array(12));

        const isProd = import.meta.env.MODE === 'prod';

        if (!process.env.AAD && isProd) {
            throw new Error('Missing required environment variable AAD. Please set process.env.AAD');
        }

        const aadStr = process.env.AAD || 'RandomAADValue';
        const aadBuf = encoder.encode(aadStr);

        const ivConcat = new Uint8Array(iv12.length + aadBuf.length);
        ivConcat.set(iv12, 0);
        ivConcat.set(aadBuf, iv12.length);

        /* import cek into web crypto */
        const cekKey = await crypto.subtle.importKey('raw', cekRaw, { name: 'AES-GCM' }, false, ['encrypt']);

        // encrypt JSON payload
        const plaintext = encoder.encode(JSON.stringify(data));
        const encrypted = await crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv12,
                /* Optional AAD */
                additionalData: aadBuf,
                tagLength: 128,
            },
            cekKey,
            plaintext,
        );

        // sciphertext + auth–tag
        const encryptedBytes = new Uint8Array(encrypted);
        const ciphertext = encryptedBytes.subarray(0, encryptedBytes.length - 16);
        const authTag = encryptedBytes.subarray(encryptedBytes.length - 16);

        // convert to base64
        const b64IvConcat = toB64(ivConcat);
        const b64Iv12 = toB64(iv12);
        const b64Cek = toB64(cekRaw);
        const headers = await buildHeader({ b64Cek, b64IvConcat });

        // convert iv and cek to b64
        return {
            iv: b64Iv12,
            cek: b64Cek,
            aad: aadStr,
            encryptedData: `${toB64(ciphertext)}|${toB64(authTag)}`,
            headers,
        };
    } catch (error) {
        console.error('Encryption process failed:', error);
        throw new Error('Encryption failed. Please check data.');
    }
};
